import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { WagonWheel } from "@/components/WagonWheel";
import { PitchMap } from "@/components/PitchMap";
import { AudioRecorder } from "@/components/AudioRecorder";
import { VoiceDictate } from "@/components/VoiceDictate";
import { NumberField, OversField } from "@/components/NumberWheel";
import { battingDerived, bowlingDerived, oversToBalls, ballsToOvers } from "@/utils/metrics";
import { enqueueEntry } from "@/lib/offlineQueue";
import { toast } from "sonner";
import { Trophy, Dumbbell, ChevronDown, AlertTriangle, Save } from "lucide-react";

const FORMATS = ["T20", "ODI", "T10", "Test", "Custom"];
const PRACTICE_TYPES = ["nets", "throwdowns", "match-sim", "fitness"];
const DISMISSALS = ["not out", "bowled", "caught", "lbw", "run out", "stumped", "hit wicket", "caught & bowled"];
const DRAFT_KEY = "ct_draft";

const blank = {
  entry_type: "tournament", format: "T20", practice_type: "nets",
  date: new Date().toISOString().slice(0, 10), opponent: "", tournament: "", team: "", venue: "",
  batting: null, bowling: null, fielding: null, reflections: {},
};

function SectionCard({ title, color, enabled, onToggle, children, testid }) {
  const t = useT();
  return (
    <div className="ct-card overflow-hidden" data-testid={testid}>
      <button type="button" onClick={onToggle} data-testid={`${testid}-toggle`} className="w-full flex items-center justify-between p-4" style={{ borderLeft: `4px solid ${color}` }}>
        <span className="font-head font-bold text-xl">{t(title)}</span>
        <span className={`text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full ${enabled ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>{enabled ? t("Logging") : t("Add")}</span>
      </button>
      {enabled && <div className="p-4 pt-0 fade-up">{children}</div>}
    </div>
  );
}

function MatchNotes({ section = {}, onChange, testid }) {
  const t = useT();
  return (
    <div className="bg-slate-50 rounded-xl p-3" data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <Label className="text-xs">{t("Match notes")} <span className="text-slate-400 font-normal">· {t("private")}</span></Label>
        <div className="flex items-center gap-2">
          <VoiceDictate testid={`${testid}-dictate`} onText={(txt) => onChange({ notes: (section.notes ? section.notes + " " : "") + txt })} />
          <AudioRecorder value={section.notes_audio} onChange={(v) => onChange({ notes_audio: v })} testid={`${testid}-audio`} />
        </div>
      </div>
      <textarea className="w-full rounded-lg border border-slate-200 p-2 text-sm" rows={2} value={section.notes || ""} onChange={(e) => onChange({ notes: e.target.value })} data-testid={`${testid}-text`} placeholder={t("Any match-specific update — conditions, plans, what worked…")} />
    </div>
  );
}

export default function LogPerformance() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const t = useT();
  const [entry, setEntry] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [dupWarn, setDupWarn] = useState(false);
  const [savedDraft, setSavedDraft] = useState(false);
  const [hand, setHand] = useState("Right-hand");
  const [clubs, setClubs] = useState([]);
  useEffect(() => { api.get("/profile/me").then(({ data }) => { setHand(data.profile?.batting_hand || "Right-hand"); setClubs(data.profile?.clubs || []); }).catch(() => {}); }, []);

  // load edit or draft
  useEffect(() => {
    if (id) {
      api.get(`/entries/${id}`).then(({ data }) => setEntry({ ...blank, ...data }));
    } else {
      const d = localStorage.getItem(DRAFT_KEY);
      if (d) { try { setEntry({ ...blank, ...JSON.parse(d) }); setSavedDraft(true); } catch {} }
    }
  }, [id]);

  // autosave draft
  useEffect(() => {
    if (id) return;
    const t = setTimeout(() => { localStorage.setItem(DRAFT_KEY, JSON.stringify(entry)); setSavedDraft(true); }, 600);
    return () => clearTimeout(t);
  }, [entry, id]);

  // duplicate check
  useEffect(() => {
    if (id || !entry.opponent) { setDupWarn(false); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/entries?entry_type=${entry.entry_type}&date_from=${entry.date}&date_to=${entry.date}`);
        setDupWarn(data.some((e) => e.opponent === entry.opponent));
      } catch { setDupWarn(false); }
    }, 800);
    return () => clearTimeout(t);
  }, [entry.date, entry.opponent, entry.entry_type, id]);

  const set = (patch) => setEntry((p) => ({ ...p, ...patch }));
  const setBat = (patch) => setEntry((p) => ({ ...p, batting: { ...p.batting, ...patch } }));
  const setBowl = (patch) => setEntry((p) => ({ ...p, bowling: { ...p.bowling, ...patch } }));

  const batD = useMemo(() => battingDerived(entry.batting || {}), [entry.batting]);
  const bowlD = useMemo(() => bowlingDerived(entry.bowling || {}), [entry.bowling]);

  const computedBalls = oversToBalls(entry.bowling?.overs);
  const overMismatch = entry.bowling?.balls_override && Number(entry.bowling.balls_override) !== computedBalls;

  // sync wicket rows with wickets count
  useEffect(() => {
    if (!entry.bowling) return;
    const w = Number(entry.bowling.wickets) || 0;
    const rows = entry.bowling.wicket_rows || [];
    if (rows.length !== w) {
      const next = Array.from({ length: w }).map((_, i) => rows[i] || { type: "bowled", batsman: "", runs: 0 });
      setBowl({ wicket_rows: next });
    }
  }, [entry.bowling?.wickets]);

  const save = async (another = false) => {
    setBusy(true);
    try {
      const payload = { ...entry };
      if (id) {
        await api.put(`/entries/${id}`, payload);
        toast.success(t("Entry updated"));
      } else {
        const offline = typeof navigator !== "undefined" && navigator.onLine === false;
        if (offline) {
          enqueueEntry(payload);
          toast.success(t("Saved offline — will sync when you're back online"));
        } else {
          try {
            await api.post("/entries", payload);
            toast.success(t("Performance logged!"));
          } catch (err) {
            if (!err.response) {
              enqueueEntry(payload);
              toast.success(t("Saved offline — will sync when you're back online"));
            } else {
              throw err;
            }
          }
        }
        localStorage.removeItem(DRAFT_KEY);
      }
      if (another) { setEntry({ ...blank, date: entry.date, tournament: entry.tournament, team: entry.team, entry_type: entry.entry_type }); window.scrollTo(0, 0); }
      else navigate("/app/entries");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const isTour = entry.entry_type === "tournament";
  const isPractice = entry.entry_type === "practice";

  return (
    <div className="max-w-3xl mx-auto" data-testid="logging-form-container">
      <div className="flex items-center justify-between mb-5">
        <h1 className="font-head font-bold text-3xl">{id ? "Edit Entry" : t("Log Performance")}</h1>
        {!id && <span className="text-xs text-slate-500 flex items-center gap-1" data-testid="draft-status-badge"><Save size={12} />{savedDraft ? "Draft saved" : "…"}</span>}
      </div>

      {/* type tabs */}
      <div className="grid grid-cols-2 gap-2 mb-5">
        <button onClick={() => set({ entry_type: "tournament" })} data-testid="tab-tournament"
          className={`flex items-center justify-center gap-2 rounded-xl p-4 font-head font-bold text-lg border-2 transition-all ${isTour ? "border-brand bg-brand text-white" : "border-slate-200 bg-white"}`}>
          <Trophy size={20} /> {t("Tournament")}
        </button>
        <button onClick={() => set({ entry_type: "practice", batting: null, bowling: null, fielding: null })} data-testid="tab-practice"
          className={`flex items-center justify-center gap-2 rounded-xl p-4 font-head font-bold text-lg border-2 transition-all ${isPractice ? "border-ink bg-ink text-white" : "border-slate-200 bg-white"}`}>
          <Dumbbell size={20} /> {t("Practice")}
        </button>
      </div>

      {/* common fields */}
      <div className="ct-card p-4 mb-4 grid sm:grid-cols-2 gap-3">
        <div><Label className="text-xs">{t("Date")}</Label><Input type="date" value={entry.date} onChange={(e) => set({ date: e.target.value })} data-testid="input-date" className="mt-1" /></div>
        {isTour ? (
          <div><Label className="text-xs">{t("Format")}</Label><select className="w-full h-9 rounded-md border border-slate-200 px-2 mt-1 text-sm" value={entry.format || ""} onChange={(e) => set({ format: e.target.value })} data-testid="input-format">{FORMATS.map((f) => <option key={f}>{f}</option>)}</select></div>
        ) : (
          <div><Label className="text-xs">{t("Practice type")}</Label><select className="w-full h-9 rounded-md border border-slate-200 px-2 mt-1 text-sm" value={entry.practice_type || ""} onChange={(e) => set({ practice_type: e.target.value })} data-testid="input-practice-type">{PRACTICE_TYPES.map((f) => <option key={f} value={f}>{t(f)}</option>)}</select></div>
        )}
        <div><Label className="text-xs">{isTour ? t("Opponent") : t("Session focus")}</Label><Input value={entry.opponent} onChange={(e) => set({ opponent: e.target.value })} data-testid="input-opponent" className="mt-1" placeholder={isTour ? "e.g. Dadar Union" : "e.g. Pace bowling"} /></div>
        <div><Label className="text-xs">{isTour ? t("Tournament") : t("Venue")}</Label><Input value={isTour ? entry.tournament : entry.venue} onChange={(e) => set(isTour ? { tournament: e.target.value } : { venue: e.target.value })} data-testid="input-tournament" className="mt-1" /></div>
        <div className="sm:col-span-2">
          <Label className="text-xs">{t("Playing for (team)")}</Label>
          {clubs.length > 0 ? (
            <div className="flex gap-2 mt-1">
              <select className="flex-1 h-9 rounded-md border border-slate-200 px-2 text-sm" value={clubs.includes(entry.team) ? entry.team : (entry.team ? "__custom__" : "")} onChange={(e) => set({ team: e.target.value === "__custom__" ? "" : e.target.value })} data-testid="input-team">
                <option value="">{t("Select your team…")}</option>
                {clubs.map((c) => <option key={c} value={c}>{c}</option>)}
                <option value="__custom__">{t("Other (type below)")}</option>
              </select>
              {!clubs.includes(entry.team) && <Input value={entry.team} onChange={(e) => set({ team: e.target.value })} placeholder={t("Team name")} className="flex-1" data-testid="input-team-custom" />}
            </div>
          ) : (
            <Input value={entry.team} onChange={(e) => set({ team: e.target.value })} placeholder="e.g. Shivaji Park Gymkhana" data-testid="input-team" className="mt-1" />
          )}
        </div>
      </div>

      {dupWarn && <div className="mb-4 flex items-center gap-2 text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-3" data-testid="duplicate-warning"><AlertTriangle size={16} /> You already have an entry vs "{entry.opponent}" on this date.</div>}

      <div className="space-y-4">
        {isTour && (<>
        {/* BATTING */}
        <SectionCard title="Batting" color="#DC2626" enabled={!!entry.batting} onToggle={() => set({ batting: entry.batting ? null : { runs: 0, balls: 0, fours: 0, sixes: 0, dismissal: "not out", not_out: true, shots: [] } })} testid="batting-section">
          {entry.batting && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <NumberField label="Position" value={entry.batting.position} min={1} max={11} onChange={(v) => setBat({ position: v })} testid="input-batting-position" />
                <NumberField label="Runs" value={entry.batting.runs} max={400} onChange={(v) => setBat({ runs: v })} testid="input-batting-runs" />
                <NumberField label="Balls" value={entry.batting.balls} max={400} onChange={(v) => setBat({ balls: v })} testid="input-batting-balls" />
                <NumberField label="Dots" value={entry.batting.dots} max={400} onChange={(v) => setBat({ dots: v })} testid="input-batting-dots" />
                <NumberField label="Fours" value={entry.batting.fours} max={60} onChange={(v) => setBat({ fours: v })} testid="input-batting-fours" />
                <NumberField label="Sixes" value={entry.batting.sixes} max={60} onChange={(v) => setBat({ sixes: v })} testid="input-batting-sixes" />
                <div className="col-span-2"><Label className="text-xs">{t("Dismissal")}</Label><select className="w-full h-9 rounded-md border border-slate-200 px-2 mt-1 text-sm" value={entry.batting.dismissal} onChange={(e) => setBat({ dismissal: e.target.value, not_out: e.target.value === "not out" })} data-testid="input-dismissal">{DISMISSALS.map((d) => <option key={d} value={d}>{t(d)}</option>)}</select></div>
              </div>
              <div className="bg-slate-50 rounded-xl p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Wagon Wheel · {hand}</span>
                  <span className="text-xs text-slate-500">Mapped {(entry.batting.shots || []).reduce((a, s) => a + (s.runs || 0), 0)} of {entry.batting.runs || 0}</span>
                </div>
                <div className="flex flex-col items-center gap-3">
                  <WagonWheel shots={entry.batting.shots || []} hand={hand} dismissalMode={entry.batting._dismissalMode}
                    onAdd={(s) => { if (s.is_dismissal) setBat({ dismissal_point: s, _dismissalMode: false }); else setBat({ shots: [...(entry.batting.shots || []), s] }); }} />
                  <div className="flex gap-2">
                    <Button type="button" size="sm" variant={entry.batting._dismissalMode ? "default" : "outline"} className={entry.batting._dismissalMode ? "bg-brand" : ""} onClick={() => setBat({ _dismissalMode: !entry.batting._dismissalMode })} data-testid="dismissal-mode-btn">Mark dismissal</Button>
                    {(entry.batting.shots || []).length > 0 && <Button type="button" size="sm" variant="ghost" onClick={() => setBat({ shots: entry.batting.shots.slice(0, -1) })}>Undo shot</Button>}
                  </div>
                </div>
              </div>
              <MatchNotes section={entry.batting} onChange={setBat} testid="batting-match-notes" />
            </div>
          )}
        </SectionCard>
        <SectionCard title="Bowling" color="#1E40AF" enabled={!!entry.bowling} onToggle={() => set({ bowling: entry.bowling ? null : { overs: "0.0", maidens: 0, runs_conceded: 0, wickets: 0, wides: 0, noballs: 0, dots: 0, wicket_rows: [], pitch_map: [] } })} testid="bowling-section">
          {entry.bowling && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <OversField value={entry.bowling.overs} onChange={(v) => setBowl({ overs: v })} computedBalls={computedBalls} testid="overs-field" />
                <NumberField label="Maidens" value={entry.bowling.maidens} max={20} onChange={(v) => setBowl({ maidens: v })} testid="input-maidens" />
                <NumberField label="Runs" value={entry.bowling.runs_conceded} max={300} onChange={(v) => setBowl({ runs_conceded: v })} testid="input-runs-conceded" />
                <NumberField label="Wickets" value={entry.bowling.wickets} max={10} onChange={(v) => setBowl({ wickets: v })} testid="input-wickets" />
                <NumberField label="Wides" value={entry.bowling.wides} max={50} onChange={(v) => setBowl({ wides: v })} testid="input-wides" />
                <NumberField label="No-balls" value={entry.bowling.noballs} max={50} onChange={(v) => setBowl({ noballs: v })} testid="input-noballs" />
                <NumberField label="Dots" value={entry.bowling.dots} max={300} onChange={(v) => setBowl({ dots: v })} testid="input-bowling-dots" />
                <NumberField label="Balls override" value={entry.bowling.balls_override} max={300} onChange={(v) => setBowl({ balls_override: v })} testid="input-balls-override" />
              </div>
              {overMismatch && <div className="flex items-center gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-2" data-testid="overs-mismatch"><AlertTriangle size={14} /> Override ({entry.bowling.balls_override}) doesn't match overs ({computedBalls} balls).</div>}
              {(entry.bowling.wicket_rows || []).length > 0 && (
                <div className="bg-slate-50 rounded-xl p-3 space-y-2">
                  <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Wicket breakdown ({entry.bowling.wicket_rows.length})</span>
                  {entry.bowling.wicket_rows.map((row, i) => (
                    <div key={i} className="grid grid-cols-2 gap-2" data-testid={`wicket-row-${i}`}>
                      <select className="h-8 rounded border border-slate-200 text-sm px-2" value={row.type} onChange={(e) => { const r = [...entry.bowling.wicket_rows]; r[i] = { ...r[i], type: e.target.value }; setBowl({ wicket_rows: r }); }}>{["bowled", "caught", "lbw", "stumped", "run out", "hit wicket"].map((d) => <option key={d} value={d}>{t(d)}</option>)}</select>
                      <Input placeholder="Batsman (optional)" value={row.batsman || ""} onChange={(e) => { const r = [...entry.bowling.wicket_rows]; r[i] = { ...r[i], batsman: e.target.value }; setBowl({ wicket_rows: r }); }} className="h-8" />
                    </div>
                  ))}
                </div>
              )}
              <div className="bg-slate-50 rounded-xl p-3">
                <div className="flex items-center justify-between mb-2"><span className="text-xs font-bold uppercase tracking-widest text-slate-500">Pitch Map</span>{(entry.bowling.pitch_map || []).length > 0 && <button type="button" className="text-xs text-brand" onClick={() => setBowl({ pitch_map: entry.bowling.pitch_map.slice(0, -1) })}>Undo</button>}</div>
                <PitchMap cells={entry.bowling.pitch_map || []} onAdd={(c) => setBowl({ pitch_map: [...(entry.bowling.pitch_map || []), c] })} />
              </div>
              <MatchNotes section={entry.bowling} onChange={setBowl} testid="bowling-match-notes" />
            </div>
          )}
        </SectionCard>

        {/* FIELDING */}
        <SectionCard title="Fielding" color="#D97706" enabled={!!entry.fielding} onToggle={() => set({ fielding: entry.fielding ? null : { catches: 0, runouts: 0, stumpings: 0, drops: 0 } })} testid="fielding-section">
          {entry.fielding && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <NumberField label="Catches" value={entry.fielding.catches} max={30} onChange={(v) => set({ fielding: { ...entry.fielding, catches: v } })} testid="input-catches" />
                <NumberField label="Run-outs" value={entry.fielding.runouts} max={30} onChange={(v) => set({ fielding: { ...entry.fielding, runouts: v } })} testid="input-runouts" />
                <NumberField label="Stumpings" value={entry.fielding.stumpings} max={30} onChange={(v) => set({ fielding: { ...entry.fielding, stumpings: v } })} testid="input-stumpings" />
                <NumberField label="Drops" value={entry.fielding.drops} max={30} onChange={(v) => set({ fielding: { ...entry.fielding, drops: v } })} testid="input-drops" />
              </div>
              <MatchNotes section={entry.fielding} onChange={(patch) => set({ fielding: { ...entry.fielding, ...patch } })} testid="fielding-match-notes" />
            </div>
          )}
        </SectionCard>
        </>)}

        {/* REFLECTIONS (practice) */}
        {isPractice && (
          <div className="ct-card p-4 space-y-4" data-testid="reflections-section">
            <h3 className="font-head font-bold text-xl">{t("Reflections")} <span className="text-xs font-body font-normal text-slate-400">· {t("private")}</span></h3>
            {[
              { key: "coach_obs", audio: "coach_obs_audio", label: "Coach observations" },
              { key: "self_assessment", audio: "self_assessment_audio", label: "Self-assessment" },
              { key: "takeaways", audio: "takeaways_audio", label: "Key takeaways" },
            ].map((f) => (
              <div key={f.key}>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs">{t(f.label)}</Label>
                  <div className="flex items-center gap-2">
                    <VoiceDictate testid={`dictate-${f.key}`} onText={(txt) => set({ reflections: { ...entry.reflections, [f.key]: (entry.reflections?.[f.key] ? entry.reflections[f.key] + " " : "") + txt } })} />
                    <AudioRecorder value={entry.reflections?.[f.audio]} onChange={(v) => set({ reflections: { ...entry.reflections, [f.audio]: v } })} testid={`audio-${f.key}`} />
                  </div>
                </div>
                <textarea className="w-full rounded-lg border border-slate-200 p-2 text-sm" rows={2} value={entry.reflections?.[f.key] || ""} onChange={(e) => set({ reflections: { ...entry.reflections, [f.key]: e.target.value } })} data-testid={`reflection-${f.key}`} />
              </div>
            ))}
            <NumberField label="Self rating (1-10)" value={entry.reflections?.self_rating || 5} min={1} max={10} onChange={(v) => set({ reflections: { ...entry.reflections, self_rating: v } })} testid="input-self-rating" />
          </div>
        )}
      </div>

      {/* sticky derived strip */}
      {isTour && (
      <div className="sticky bottom-16 md:bottom-4 mt-6 z-30" data-testid="sticky-derived-metrics-strip">
        <div className="bg-ink text-white rounded-xl p-3 grid grid-cols-4 gap-2 shadow-2xl grain">
          <Metric label={t("Strike Rate")} value={entry.batting ? batD.strike_rate : "-"} />
          <Metric label={t("Boundary %")} value={entry.batting ? batD.boundary_pct : "-"} />
          <Metric label={t("Economy")} value={entry.bowling ? bowlD.economy : "-"} />
          <Metric label={t("Dot %")} value={entry.bowling ? bowlD.dot_pct : (entry.batting ? batD.dot_pct : "-")} />
        </div>
      </div>
      )}

      <div className="flex flex-wrap gap-3 mt-4">
        <Button disabled={busy} onClick={() => save(false)} className="bg-brand hover:bg-brand-dark px-8 h-11" data-testid="save-entry-btn">{busy ? "…" : id ? t("Update entry") : t("Save entry")}</Button>
        {!id && <Button disabled={busy} variant="outline" onClick={() => save(true)} className="h-11" data-testid="log-another-btn">{t("Save & log another")}</Button>}
        {!id && <Button variant="ghost" onClick={() => { localStorage.removeItem(DRAFT_KEY); setEntry(blank); toast("Draft cleared"); }} className="h-11">{t("Clear draft")}</Button>}
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="text-center">
      <div className="stat-num text-2xl text-brand tabular">{value}</div>
      <div className="text-[9px] uppercase tracking-widest text-slate-400">{label}</div>
    </div>
  );
}
